import { HttpPermission } from '@/js/http-Manager'

// module-level inflight Promise：所有並行 dispatch 共用同一個 fetch，
// 避免「第二次 dispatch 拿到 undefined → await 立即通過 → can() 在 loaded=false 時 fail-closed 誤判」
let inflightLoadPromise = null
let loadGeneration = 0

// EGKCRM-514 PR-2: 使用者 effective permissions 快取 + $can helper 來源
const state = {
  // Map: "resourceKey:action" -> { isGranted, scope, source }
  effectiveMap: {},
  loaded: false,
  loadError: false,
  // router guard 偵測到無權限訪問時暫存 { resource, action }，由 App.vue snackbar 消費後清回 null
  accessDenied: null
}

const getters = {
  isLoaded (state) {
    return state.loaded
  },
  can: (state) => (resource, action) => {
    // fail-closed — 跟後端 default-deny 對齊
    // 未載入 / 未配置一律 deny，避免 network fail 或 race 期間誤放行管理頁面
    if (!state.loaded) return false
    const entry = state.effectiveMap[`${resource}:${action}`]
    if (!entry) return false
    // Legacy 軌（UseLegacyPermission=true）的 entry.isGranted 是 inheritedValue（永遠 false），
    // 但真正判斷由後端 LegacyChecker 完成；前端 $can 僅用於 sidebar/button 顯示，
    // 若嚴格擋會誤擋 Admin 看 Legacy resource 的入口。UI-level 放行，後端 [Permission] 仍擋非 Admin 請求
    // 用 isLegacy boolean 而非比對 source 字串，避免後端欄位名稱改動時靜默失效
    if (entry.isLegacy) return true
    return entry.isGranted === true
  },
  getScope: (state) => (resource, action = 'view') => {
    const key = `${resource}:${action}`
    const entry = state.effectiveMap[key]
    return entry ? entry.scope : null
  }
}

function normalizePermissionList (payload) {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.Data)) return payload.Data
  if (payload && Array.isArray(payload.data)) return payload.data
  return []
}

const actions = {
  load ({ commit, state }, userId) {
    if (inflightLoadPromise) return inflightLoadPromise
    if (state.loaded) return Promise.resolve()
    const generation = loadGeneration
    const loadPromise = (async () => {
      try {
        commit('setLoadError', false)
        const response = await HttpPermission.GetEffectivePermissions(userId)
        if (generation !== loadGeneration) return
        if (response && response.status === 200) {
          commit('setPermissions', normalizePermissionList(response.data))
        } else {
          commit('setLoadError', true)
        }
      } catch (e) {
        if (generation !== loadGeneration) return
        console.error('load effective permissions failed', e)
        commit('setLoadError', true)
      } finally {
        if (inflightLoadPromise === loadPromise) {
          inflightLoadPromise = null
        }
      }
    })()
    inflightLoadPromise = loadPromise
    return inflightLoadPromise
  },
  reset ({ commit }) {
    loadGeneration++
    inflightLoadPromise = null
    commit('reset')
  }
}

const mutations = {
  setPermissions (state, list) {
    const map = {}
    const permissions = Array.isArray(list) ? list : []
    for (const p of permissions) {
      map[`${p.resourceKey}:${p.action}`] = {
        isGranted: p.isGranted,
        scope: p.scope,
        source: p.source,
        isLegacy: p.isLegacy === true
      }
    }
    state.effectiveMap = map
    state.loaded = true
  },
  reset (state) {
    state.effectiveMap = {}
    state.loaded = false
    state.loadError = false
    state.accessDenied = null
  },
  setLoadError (state, flag) {
    state.loadError = flag === true
  },
  setAccessDenied (state, payload) {
    state.accessDenied = payload || null
  },
  // ResourceSwitcher 切 UseLegacyPermission 後立即同步對應 resource 所有 action 的 isLegacy，
  // 讓 sidebar / router guard / UsersView 的 can 立刻反映，不用等整個 map 重 load
  setResourceLegacyFlag (state, { resourceKey, isLegacy }) {
    const next = { ...state.effectiveMap }
    const prefix = `${resourceKey}:`
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) {
        next[k] = { ...next[k], isLegacy: isLegacy === true }
      }
    }
    state.effectiveMap = next
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
